import { useCallback, useRef, useState } from 'react';

import { CellKeyDownEvent, GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { TOA_GetFertilizationsForFreezing } from '@/rtk-query.gql';

import { SelectionService } from '../../utils/selectionService';

export const useGridOperations = () => {
  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] =
    useState<GridApi<TOA_GetFertilizationsForFreezing> | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [quickSearchValue, setQuickSearchValue] = useState('');

  const handleExpandAll = useCallback(
    async (forceState?: boolean) => {
      const newExpandedState =
        forceState !== undefined ? forceState : !isExpanded;

      if (gridRef.current?.api) {
        const api = gridRef.current.api;
        const selectedFertIds =
          SelectionService.getSelection().fertilizationIds;

        if (newExpandedState) {
          dispatch(
            addToast({
              severity: 'info',
              summary: 'Expanding Rows',
              detail: 'Please wait while rows are being expanded.',
              sticky: false,
            }),
          );

          for (const fertId of selectedFertIds) {
            const node = api.getRowNode(fertId);
            if (node && typeof node.setExpanded === 'function') {
              node.setExpanded(true);
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
        } else {
          api.forEachNode((node) => {
            node.setExpanded(false);
          });
        }
        setIsExpanded(newExpandedState);

        if (newExpandedState) {
          dispatch(
            addToast({
              severity: 'success',
              summary: 'Rows Expanded',
              detail: 'Rows are now expanded.',
              sticky: false,
            }),
          );
        }
      }
    },
    [dispatch, isExpanded],
  );

  const onCellKeyDown = useCallback((e: CellKeyDownEvent) => {
    if ((e.event as KeyboardEvent)?.key === 'Enter') {
      const currentCell = e.api.getFocusedCell();
      if (currentCell) {
        const nextRowIndex = currentCell.rowIndex + 1;
        e.api.setFocusedCell(nextRowIndex, currentCell.column);
        e.api.clearRangeSelection();
      }
    }
  }, []);

  const handleFertilizationSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes();
    if (!selectedNodes) return;

    const selectedFertIds = selectedNodes.map((node) => node.data.fertId);
    SelectionService.updateSelection('fert', selectedFertIds);
  }, []);

  const selectFertForLiveEmbryos = useCallback((fertId: string) => {
    const selection = SelectionService.getSelection();

    const rowNode = gridRef?.current?.api.getRowNode(fertId);
    if (rowNode) {
      if (selection.liveEmbryoIds.length > 0) {
        rowNode.setSelected(true);
        if (!selection.fertilizationIds.includes(fertId)) {
          SelectionService.updateSelection('fert', [
            ...selection.fertilizationIds,
            fertId,
          ]);
        }
      } else {
        rowNode.setSelected(false);
        SelectionService.updateSelection(
          'fert',
          selection.fertilizationIds.filter((id) => id !== fertId),
        );
      }
    }
  }, []);

  const selectFertForTempEmbryos = useCallback((fertId: string) => {
    const selection = SelectionService.getSelection();

    const rowNode = gridRef?.current?.api.getRowNode(fertId);
    if (rowNode) {
      if (selection.tempEmbryoIds.length > 0) {
        rowNode.setSelected(true);
        if (!selection.fertilizationIds.includes(fertId)) {
          SelectionService.updateSelection('fert', [
            ...selection.fertilizationIds,
            fertId,
          ]);
        }
      } else {
        rowNode.setSelected(false);
        SelectionService.updateSelection(
          'fert',
          selection.fertilizationIds.filter((id) => id !== fertId),
        );
      }
    }
  }, []);

  const handleEmbryoGridEdit = useCallback((fertId: string) => {
    if (gridRef.current?.api) {
      const rowNode = gridRef.current.api.getRowNode(fertId);
      if (rowNode) {
        rowNode.setSelected(true);
      }
    }
  }, []);

  return {
    gridRef,
    gridApi,
    setGridApi,
    isExpanded,
    quickSearchValue,
    setQuickSearchValue,
    handleExpandAll,
    onCellKeyDown,
    handleFertilizationSelectionChanged,
    selectFertForLiveEmbryos,
    selectFertForTempEmbryos,
    handleEmbryoGridEdit,
  };
};
